import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Search as SearchIcon, Users as UsersIcon, Loader2 } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { Input } from "@/components/ui/input";
import { PeopleList } from "@/components/people-list";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { discoverPeople, fetchFollowingIds, type PersonSummary } from "@/lib/follows";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

export const Route = createFileRoute("/search")({
  head: () => {
    const title = "Xamseen — Discover people and communities";
    const description =
      "Find people, communities and posts on Xamseen. Follow the neighbours, officials and organisations shaping the places you care about.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
    };
  },
  component: DiscoverPage,
});

interface CommunityRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  city: string | null;
  country: string | null;
  member_count: number | null;
}

interface PostRow {
  id: string;
  title: string | null;
  content: string | null;
  created_at: string;
  location_name: string | null;
}

type Tab = "people" | "organisations" | "communities" | "posts";

function DiscoverPage() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<Tab>("people");
  const [people, setPeople] = useState<PersonSummary[]>([]);
  const [comms, setComms] = useState<CommunityRow[]>([]);
  const [posts, setPosts] = useState<PostRow[]>([]);
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    fetchFollowingIds(user.id).then(setFollowingIds);
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      const like = `%${q.trim()}%`;
      if (tab === "people" || tab === "organisations") {
        const rows = await discoverPeople({
          query: q,
          viewerId: user?.id,
          official: tab === "organisations",
          limit: 40,
        });
        if (!cancelled) setPeople(rows);
      } else if (tab === "communities") {
        let query = supabase
          .from("communities")
          .select("id, slug, name, description, city, country, member_count");
        if (q.trim())
          query = query.or(`name.ilike.${like},description.ilike.${like},city.ilike.${like}`);
        const { data } = await query.order("member_count", { ascending: false }).limit(40);
        if (!cancelled) setComms((data ?? []) as CommunityRow[]);
      } else {
        let query = supabase
          .from("posts")
          .select("id, title, content, created_at, location_name")
          .eq("hidden", false);
        if (q.trim()) query = query.or(`title.ilike.${like},content.ilike.${like}`);
        const { data } = await query.order("created_at", { ascending: false }).limit(30);
        if (!cancelled) setPosts((data ?? []) as PostRow[]);
      }
      if (!cancelled) setLoading(false);
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, tab, user?.id]);

  const tabs = useMemo<[Tab, string][]>(
    () => [
      ["people", "People"],
      ["organisations", "Organisations"],
      ["communities", "Communities"],
      ["posts", "Posts"],
    ],
    [],
  );

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl">
        <h1 className="font-display text-2xl font-bold tracking-tight">Discover</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Follow the people, officials and communities shaping the places you care about.
        </p>

        <div className="relative mt-5">
          <SearchIcon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people, communities, places"
            className="h-12 rounded-full pl-10 text-base"
          />
        </div>

        <div className="no-scrollbar mt-4 flex gap-2 overflow-x-auto">
          {tabs.map(([value, label]) => (
            <button
              key={value}
              onClick={() => setTab(value)}
              aria-pressed={tab === value}
              className={cn(
                "min-h-11 shrink-0 rounded-full border px-4 text-sm font-medium transition-colors",
                tab === value
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:bg-muted",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
            className="mt-2 divide-y divide-border"
          >
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : tab === "people" || tab === "organisations" ? (
              <PeopleList
                people={people}
                followingIds={followingIds}
                empty={
                  tab === "organisations"
                    ? "No verified organisations match that."
                    : "Nobody matches that yet."
                }
                onFollowChange={(id, following) =>
                  setFollowingIds((prev) => {
                    const next = new Set(prev);
                    if (following) next.add(id);
                    else next.delete(id);
                    return next;
                  })
                }
              />
            ) : tab === "communities" ? (
              comms.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  No communities match that.
                </p>
              ) : (
                <ul>
                  {comms.map((c) => (
                    <li key={c.id}>
                      <Link
                        to="/communities/$slug"
                        params={{ slug: c.slug }}
                        className="flex items-center gap-3 py-4"
                      >
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-muted">
                          <UsersIcon className="h-5 w-5 text-muted-foreground" />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate font-semibold">{c.name}</span>
                          <span className="block truncate text-[13px] text-muted-foreground">
                            {[c.city, c.country].filter(Boolean).join(", ") || "Community"} ·{" "}
                            {c.member_count ?? 0} members
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )
            ) : posts.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                Nothing matches that yet.
              </p>
            ) : (
              <ul>
                {posts.map((p) => (
                  <li key={p.id}>
                    <Link to="/posts/$id" params={{ id: p.id }} className="block py-4">
                      <span className="block font-semibold">{p.title}</span>
                      <span className="mt-0.5 line-clamp-2 block text-[13px] text-muted-foreground">
                        {p.content}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </AppShell>
  );
}
